import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, MailOpen, X } from "lucide-react";
import { useLang } from "@/i18n";
import { api, formatApiError } from "@/api";

const inputCls =
  "w-full bg-obsidian border border-slate-700 rounded-sm px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold/70 transition-colors duration-300";

const EMPTY_OPP = {
  title_fr: "", title_en: "", country: "", sector: "", type_fr: "", type_en: "",
  amount: "", description_fr: "", description_en: "", profile_fr: "", profile_en: "",
  status: "open", visibility: "public",
};
const EMPTY_SECTOR = { key: "", name_fr: "", name_en: "", icon: "Globe", order: 0 };

const VisBadge = ({ v }) => {
  const map = {
    public: "border-emerald-500/40 text-emerald-400",
    confidential: "border-amber-500/40 text-amber-400",
    private: "border-red-500/40 text-red-400",
  };
  return <span className={`px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded-full border ${map[v]}`}>{v}</span>;
};

function Messages({ t }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/messages").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="admin-messages-list">
      {items.length === 0 && <p className="text-sm text-slate-500">{t.admin.noMessages}</p>}
      {items.map((m) => (
        <div key={m.id} data-testid={`admin-message-${m.id}`} className={`border rounded-sm p-5 ${m.read ? "border-slate-800 bg-obsidian-surface/50" : "border-gold/40 bg-obsidian-surface"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="text-slate-100 font-medium">{m.name}</span>
              <span className="text-slate-500">{m.organization}</span>
              <a href={`mailto:${m.email}`} className="text-gold/80 hover:text-gold">{m.email}</a>
              <span className="px-2 py-0.5 font-mono uppercase tracking-widest text-[10px] rounded-full border border-slate-700 text-slate-400">{m.intent}</span>
              <span className="px-2 py-0.5 font-mono uppercase tracking-widest text-[10px] rounded-full border border-slate-700 text-slate-400">{m.role}</span>
            </div>
            <div className="flex gap-2">
              {!m.read && (
                <button data-testid={`admin-message-read-${m.id}`} onClick={async () => { await api.patch(`/admin/messages/${m.id}`); load(); }}
                  className="p-2 text-slate-400 hover:text-gold transition-colors"><MailOpen size={15} /></button>
              )}
              <button data-testid={`admin-message-delete-${m.id}`} onClick={async () => { await api.delete(`/admin/messages/${m.id}`); load(); }}
                className="p-2 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-400 font-light leading-relaxed whitespace-pre-wrap">{m.message}</p>
          <p className="mt-2 text-[10px] font-mono text-slate-600">{m.country} {m.sector && `· ${m.sector}`} · {new Date(m.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function OpportunityForm({ initial, onSave, onCancel, t }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="border hairline rounded-sm bg-obsidian-surface p-6 space-y-4" data-testid="admin-opportunity-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required value={f.title_fr} onChange={set("title_fr")} placeholder="Titre (FR)" className={inputCls} data-testid="opp-form-title-fr" />
        <input required value={f.title_en} onChange={set("title_en")} placeholder="Title (EN)" className={inputCls} data-testid="opp-form-title-en" />
        <input required value={f.country} onChange={set("country")} placeholder="Pays / Country" className={inputCls} data-testid="opp-form-country" />
        <input required value={f.sector} onChange={set("sector")} placeholder="Secteur / Sector" className={inputCls} data-testid="opp-form-sector" />
        <input required value={f.type_fr} onChange={set("type_fr")} placeholder="Type (FR)" className={inputCls} data-testid="opp-form-type-fr" />
        <input required value={f.type_en} onChange={set("type_en")} placeholder="Type (EN)" className={inputCls} data-testid="opp-form-type-en" />
        <input value={f.amount || ""} onChange={set("amount")} placeholder="Montant (optionnel)" className={inputCls} data-testid="opp-form-amount" />
        <div className="flex gap-3">
          <select value={f.visibility} onChange={set("visibility")} className={inputCls} data-testid="opp-form-visibility">
            <option value="public">Public</option>
            <option value="confidential">Confidentiel</option>
            <option value="private">Strictement privé</option>
          </select>
          <select value={f.status} onChange={set("status")} className={inputCls} data-testid="opp-form-status">
            <option value="open">Ouverte</option>
            <option value="closed">Clôturée</option>
          </select>
        </div>
      </div>
      <textarea rows={3} value={f.description_fr} onChange={set("description_fr")} placeholder="Description (FR)" className={inputCls} data-testid="opp-form-desc-fr" />
      <textarea rows={3} value={f.description_en} onChange={set("description_en")} placeholder="Description (EN)" className={inputCls} data-testid="opp-form-desc-en" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input value={f.profile_fr} onChange={set("profile_fr")} placeholder="Profil recherché (FR)" className={inputCls} data-testid="opp-form-profile-fr" />
        <input value={f.profile_en} onChange={set("profile_en")} placeholder="Profile sought (EN)" className={inputCls} data-testid="opp-form-profile-en" />
      </div>
      <div className="flex gap-3">
        <button type="submit" className="px-6 py-2.5 bg-gold text-obsidian text-xs font-medium rounded-full hover:bg-gold-light transition-colors" data-testid="opp-form-save">{t.admin.save}</button>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 border border-slate-700 text-slate-300 text-xs rounded-full hover:border-slate-500 transition-colors" data-testid="opp-form-cancel">{t.admin.cancel}</button>
      </div>
    </form>
  );
}

function Opportunities({ t }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | opp
  const load = useCallback(() => api.get("/admin/opportunities").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    try {
      const payload = { ...f, amount: f.amount || null };
      if (editing === "new") await api.post("/opportunities", payload);
      else await api.put(`/opportunities/${editing.id}`, payload);
      toast.success("OK");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-opportunities-panel">
      <div className="flex justify-end">
        <button onClick={() => setEditing("new")} data-testid="admin-opportunity-new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-obsidian text-xs font-medium rounded-full hover:bg-gold-light transition-colors">
          <Plus size={14} />{t.admin.newOpportunity}
        </button>
      </div>
      {editing && (
        <OpportunityForm t={t} initial={editing === "new" ? EMPTY_OPP : editing} onSave={save} onCancel={() => setEditing(null)} />
      )}
      <div className="space-y-3">
        {items.map((o) => (
          <div key={o.id} data-testid={`admin-opportunity-${o.id}`} className="border border-slate-800 rounded-sm bg-obsidian-surface/60 p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-slate-100">{o.title_fr}</h3>
                <VisBadge v={o.visibility} />
              </div>
              <p className="mt-1.5 text-[11px] font-mono text-slate-500">{o.country} · {o.sector} · {o.type_fr} {o.amount ? `· ${o.amount}` : ""}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`admin-opportunity-edit-${o.id}`} onClick={() => setEditing(o)} className="p-2 text-slate-400 hover:text-gold transition-colors"><Pencil size={15} /></button>
              <button data-testid={`admin-opportunity-delete-${o.id}`} onClick={async () => { await api.delete(`/opportunities/${o.id}`); load(); }} className="p-2 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sectors({ t }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/sectors").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    try {
      const payload = { ...f, order: Number(f.order) || 0 };
      if (editing === "new") await api.post("/sectors", payload);
      else await api.put(`/sectors/${editing.id}`, payload);
      toast.success("OK");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sectors-panel">
      <div className="flex justify-end">
        <button onClick={() => setEditing("new")} data-testid="admin-sector-new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-obsidian text-xs font-medium rounded-full hover:bg-gold-light transition-colors">
          <Plus size={14} />{t.admin.newSector}
        </button>
      </div>
      {editing && <SectorForm t={t} initial={editing === "new" ? EMPTY_SECTOR : editing} onSave={save} onCancel={() => setEditing(null)} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((s) => (
          <div key={s.id} data-testid={`admin-sector-${s.id}`} className="border border-slate-800 rounded-sm bg-obsidian-surface/60 p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-slate-100">{s.name_fr} <span className="text-slate-500">/ {s.name_en}</span></p>
              <p className="mt-1 text-[10px] font-mono text-slate-600">{s.key} · {s.icon} · #{s.order}</p>
            </div>
            <div className="flex gap-1">
              <button data-testid={`admin-sector-edit-${s.id}`} onClick={() => setEditing(s)} className="p-2 text-slate-400 hover:text-gold transition-colors"><Pencil size={14} /></button>
              <button data-testid={`admin-sector-delete-${s.id}`} onClick={async () => { await api.delete(`/sectors/${s.id}`); load(); }} className="p-2 text-slate-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectorForm({ initial, onSave, onCancel, t }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="border hairline rounded-sm bg-obsidian-surface p-6 space-y-3" data-testid="admin-sector-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required value={f.key} onChange={set("key")} placeholder="key (slug)" className={inputCls} data-testid="sector-form-key" />
        <input value={f.icon} onChange={set("icon")} placeholder="Icône Lucide (ex: Zap)" className={inputCls} data-testid="sector-form-icon" />
        <input required value={f.name_fr} onChange={set("name_fr")} placeholder="Nom (FR)" className={inputCls} data-testid="sector-form-name-fr" />
        <input required value={f.name_en} onChange={set("name_en")} placeholder="Name (EN)" className={inputCls} data-testid="sector-form-name-en" />
        <input type="number" value={f.order} onChange={set("order")} placeholder="Ordre" className={inputCls} data-testid="sector-form-order" />
      </div>
      <div className="flex gap-3">
        <button type="submit" className="px-6 py-2.5 bg-gold text-obsidian text-xs font-medium rounded-full hover:bg-gold-light transition-colors" data-testid="sector-form-save">{t.admin.save}</button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 px-6 py-2.5 border border-slate-700 text-slate-300 text-xs rounded-full hover:border-slate-500 transition-colors" data-testid="sector-form-cancel"><X size={12} />{t.admin.cancel}</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("messages");
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("bi_admin_token")) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => setChecked(true)).catch(() => {
      localStorage.removeItem("bi_admin_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  if (!checked) return <div className="min-h-screen bg-obsidian" />;

  const tabs = [["messages", t.admin.messages], ["opportunities", t.admin.opportunities], ["sectors", t.admin.sectors]];

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="border-b hairline bg-obsidian-surface/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="flex items-center gap-2.5">
            <span className="flex items-center justify-center rounded-lg bg-gradient-to-br from-white via-[#F7F2E7] to-[#EDE4CF] px-1.5 py-1 ring-1 ring-gold/50 shadow-[0_0_16px_rgba(212,175,55,0.3)]">
              <img src="/assets/logo-emblem.png" alt="Bridge Intell" className="h-6 w-auto" />
            </span>
            <span className="font-serif tracking-[0.14em] uppercase text-slate-100">Bridge <span className="text-gold">Intell</span></span>
          </span>
          <div className="flex items-center gap-4">
            <nav className="flex gap-1" data-testid="admin-tabs">
              {tabs.map(([k, label]) => (
                <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.14em] rounded-full transition-colors duration-300 ${tab === k ? "bg-gold/10 text-gold border border-gold/40" : "text-slate-400 hover:text-slate-200 border border-transparent"}`}>
                  {label}
                </button>
              ))}
            </nav>
            <button data-testid="admin-logout" onClick={() => { localStorage.removeItem("bi_admin_token"); navigate("/admin/login"); }}
              className="p-2 text-slate-400 hover:text-red-400 transition-colors" aria-label={t.admin.logout}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {tab === "messages" && <Messages t={t} />}
        {tab === "opportunities" && <Opportunities t={t} />}
        {tab === "sectors" && <Sectors t={t} />}
      </main>
    </div>
  );
}
